// ╔══════════════════════════════════════════════════════════════╗
// ║  Shared UI primitives — Card, Btn, Input, KPI, Avatar, etc.  ║
// ╚══════════════════════════════════════════════════════════════╝

import clsx from 'clsx';
import Icon from './Icon.jsx';

// ─── Card ───
export function Card({ className, children, ...rest }) {
  return (
    <div
      className={clsx(
        'rounded-2xl bg-white dark:bg-ink-900 ring-1 ring-ink-100 dark:ring-ink-800 shadow-soft',
        className,
      )}
      {...rest}
    >
      {children}
    </div>
  );
}

// ─── Button ───
export function Btn({
  variant = 'primary',
  size = 'md',
  icon,
  iconEnd,
  className,
  children,
  loading,
  disabled,
  ...rest
}) {
  const base =
    'inline-flex items-center justify-center gap-2 font-bold rounded-xl transition active:translate-y-px disabled:opacity-50 disabled:cursor-not-allowed';
  const sizes = {
    sm: 'h-8 px-3 text-xs',
    md: 'h-10 px-4 text-sm',
    lg: 'h-12 px-6 text-base',
  };
  const variants = {
    primary:
      'bg-gradient-to-b from-brand-400 to-brand-600 text-ink-950 hover:from-brand-300 hover:to-brand-500 shadow-soft',
    outline:
      'ring-1 ring-ink-200 dark:ring-ink-700 hover:bg-ink-100 dark:hover:bg-ink-800 text-ink-700 dark:text-ink-200',
    ghost:
      'hover:bg-ink-100 dark:hover:bg-ink-800 text-ink-600 dark:text-ink-200',
    success: 'bg-emerald-500 hover:bg-emerald-600 text-white shadow-soft',
    danger: 'bg-rose-500 hover:bg-rose-600 text-white shadow-soft',
  };
  return (
    <button
      className={clsx(base, sizes[size], variants[variant], className)}
      disabled={disabled || loading}
      {...rest}
    >
      {loading ? (
        <span className="w-4 h-4 border-2 border-current border-r-transparent rounded-full animate-spin" />
      ) : icon ? (
        <Icon name={icon} size={size === 'sm' ? 12 : size === 'lg' ? 18 : 15} />
      ) : null}
      {children}
      {iconEnd && <Icon name={iconEnd} size={size === 'sm' ? 12 : 15} />}
    </button>
  );
}

// ─── Input ───
export function Input({ className, error, ...rest }) {
  return (
    <input
      className={clsx(
        'w-full h-11 px-4 rounded-xl bg-ink-50 dark:bg-ink-800',
        'ring-1 ring-ink-200 dark:ring-ink-700',
        'focus:ring-2 focus:ring-brand-400 outline-none text-sm',
        error && 'ring-rose-500 focus:ring-rose-400',
        className,
      )}
      {...rest}
    />
  );
}

// ─── Select ───
export function Select({ className, children, ...rest }) {
  return (
    <select
      className={clsx(
        'w-full h-11 px-4 rounded-xl bg-ink-50 dark:bg-ink-800',
        'ring-1 ring-ink-200 dark:ring-ink-700',
        'focus:ring-2 focus:ring-brand-400 outline-none text-sm',
        className,
      )}
      {...rest}
    >
      {children}
    </select>
  );
}

// ─── Field (label + input wrapper) ───
export function Field({ label, hint, error, children }) {
  return (
    <label className="block">
      <span className="block text-[11px] font-semibold uppercase tracking-wider text-ink-400 mb-1.5">
        {label}
      </span>
      {children}
      {error && <span className="text-[11px] text-rose-500 mt-1 block">{error}</span>}
      {hint && !error && (
        <span className="text-[11px] text-ink-400 mt-1 block">{hint}</span>
      )}
    </label>
  );
}

// ─── KPI ───
export function KPI({ label, value, sub, icon, accent, delta, foot }) {
  return (
    <Card className={clsx('p-4', accent && 'ring-brand-200 dark:ring-brand-800')}>
      <div className="flex items-start justify-between">
        <div className="min-w-0">
          <div className="text-[11px] uppercase tracking-wider text-ink-400 font-bold">
            {label}
          </div>
          <div className="text-2xl md:text-3xl font-black mt-1 tabular truncate">
            {value}
          </div>
          {sub && <div className="text-xs text-ink-400 mt-0.5 line-clamp-1">{sub}</div>}
        </div>
        {icon && (
          <span
            className={clsx(
              'w-10 h-10 rounded-xl grid place-items-center shrink-0',
              accent
                ? 'bg-brand-500 text-ink-950'
                : 'bg-ink-100 dark:bg-ink-800 text-ink-600 dark:text-ink-300',
            )}
          >
            <Icon name={icon} size={18} />
          </span>
        )}
      </div>
      {(delta !== undefined || foot) && (
        <div className="flex items-center justify-between mt-3 pt-3 border-t border-ink-100 dark:border-ink-800">
          {delta !== undefined && (
            <span
              className={clsx(
                'text-[11px] font-bold tabular',
                delta >= 0 ? 'text-emerald-600' : 'text-rose-600',
              )}
            >
              {delta >= 0 ? '+' : ''}
              {delta}%
            </span>
          )}
          {foot}
        </div>
      )}
    </Card>
  );
}

// ─── Badge ───
export function Badge({ tone = 'neutral', dot, className, children }) {
  const tones = {
    neutral: 'bg-ink-100 text-ink-700 dark:bg-ink-800 dark:text-ink-200',
    brand: 'bg-brand-100 text-brand-700 dark:bg-brand-900/40 dark:text-brand-300',
    success: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300',
    warn: 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300',
    danger: 'bg-rose-100 text-rose-700 dark:bg-rose-900/40 dark:text-rose-300',
    info: 'bg-sky-100 text-sky-700 dark:bg-sky-900/40 dark:text-sky-300',
  };
  const dotColor = {
    neutral: 'bg-ink-500',
    brand: 'bg-brand-500',
    success: 'bg-emerald-500',
    warn: 'bg-amber-500',
    danger: 'bg-rose-500',
    info: 'bg-sky-500',
  };
  return (
    <span
      className={clsx(
        'inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[11px] font-bold',
        tones[tone],
        className,
      )}
    >
      {dot && <span className={clsx('w-1.5 h-1.5 rounded-full', dotColor[tone])} />}
      {children}
    </span>
  );
}

// ─── Avatar ───
export function Avatar({ name, size = 32, className }) {
  const initials = (name || '?')
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0])
    .join('');
  return (
    <span
      className={clsx(
        'inline-flex items-center justify-center rounded-full',
        'bg-gradient-to-br from-brand-300 to-brand-600 text-ink-950 font-bold shrink-0',
        className,
      )}
      style={{ width: size, height: size, fontSize: size * 0.4 }}
    >
      {initials}
    </span>
  );
}

// ─── Progress bar ───
export function Progress({ value = 0, tone = 'brand', className }) {
  const colors = {
    brand: 'bg-brand-500',
    success: 'bg-emerald-500',
    warn: 'bg-amber-500',
    danger: 'bg-rose-500',
  };
  return (
    <div
      className={clsx(
        'flex-1 h-2 rounded-full bg-ink-100 dark:bg-ink-800 overflow-hidden',
        className,
      )}
    >
      <div
        className={clsx('h-full rounded-full transition-all', colors[tone])}
        style={{ width: `${Math.min(100, Math.max(0, value))}%` }}
      />
    </div>
  );
}

// ─── Chip ───
export function Chip({ active, onClick, children }) {
  return (
    <button
      onClick={onClick}
      className={clsx(
        'px-3 h-8 rounded-full text-xs font-bold whitespace-nowrap transition shrink-0',
        active
          ? 'bg-brand-500 text-ink-950 shadow-soft'
          : 'ring-1 ring-ink-200 dark:ring-ink-700 hover:bg-ink-100 dark:hover:bg-ink-800',
      )}
    >
      {children}
    </button>
  );
}

// ─── Pills (segmented control) ───
export function Pills({ value, onChange, items }) {
  return (
    <div className="inline-flex rounded-xl bg-ink-100 dark:bg-ink-800 p-1 gap-0.5">
      {items.map((it) => (
        <button
          key={it.value}
          onClick={() => onChange(it.value)}
          className={clsx(
            'px-3 h-8 rounded-lg text-xs font-bold transition',
            value === it.value
              ? 'bg-white dark:bg-ink-700 shadow-soft text-ink-900 dark:text-ink-50'
              : 'text-ink-500 hover:text-ink-900 dark:hover:text-ink-50',
          )}
        >
          {it.label}
        </button>
      ))}
    </div>
  );
}

// ─── Modal ───
export function Modal({ open, onClose, title, children, footer, size = 'md' }) {
  if (!open) return null;
  const sizes = {
    sm: 'max-w-sm',
    md: 'max-w-md',
    lg: 'max-w-2xl',
    xl: 'max-w-4xl',
  };
  return (
    <div
      className="fixed inset-0 z-50 grid place-items-center bg-black/50 p-4 backdrop-blur-sm fade-up"
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className={clsx(
          'bg-white dark:bg-ink-900 rounded-2xl shadow-pop w-full max-h-[90vh] flex flex-col',
          sizes[size],
        )}
      >
        <div className="flex items-center justify-between p-5 border-b border-ink-100 dark:border-ink-800">
          <h2 className="font-bold text-lg">{title}</h2>
          <button
            onClick={onClose}
            className="w-8 h-8 grid place-items-center rounded-lg hover:bg-ink-100 dark:hover:bg-ink-800 text-ink-400"
          >
            <Icon name="x" size={16} />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto p-5">{children}</div>
        {footer && (
          <div className="p-5 border-t border-ink-100 dark:border-ink-800 flex items-center justify-end gap-2">
            {footer}
          </div>
        )}
      </div>
    </div>
  );
}
